import { type RefObject, useEffect, useRef } from 'react'

export type ScrollProximityAxis = 'vertical' | 'horizontal'

/** bottom / trailing-edge prefetch margin for IntersectionObserver */
export function scrollProximityRootMargin(axis: ScrollProximityAxis, distancePx: number): string {
  const d = `${Math.max(0, Math.round(distancePx))}px`
  if (axis === 'vertical') return `0px 0px ${d} 0px`
  return `0px ${d} 0px 0px`
}

export type UseScrollProximityTriggerOptions = {
  rootRef: RefObject<Element | null>
  axis: ScrollProximityAxis
  distancePx?: number
  disabled?: boolean
  onTrigger: () => void
}

/**
 * 在可滚动容器接近尾缘时触发回调（竖向接近底部 / 横向接近右侧，LTR）。
 */
export function useScrollProximityTrigger({
  rootRef,
  axis,
  distancePx = 280,
  disabled,
  onTrigger,
}: UseScrollProximityTriggerOptions): RefObject<HTMLDivElement> {
  const sentinelRef = useRef<HTMLDivElement>(null)
  const onTriggerRef = useRef(onTrigger)
  onTriggerRef.current = onTrigger

  useEffect(() => {
    if (disabled) return
    const root = rootRef.current
    const sentinel = sentinelRef.current
    if (!root || !sentinel) return

    const rootMargin = scrollProximityRootMargin(axis, distancePx)
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) onTriggerRef.current()
      },
      { root, rootMargin, threshold: 0 },
    )
    io.observe(sentinel)
    return () => io.disconnect()
  }, [axis, disabled, distancePx, rootRef])

  return sentinelRef
}
